import Encoding from "encoding-japanese";
import * as Constants from "_core/configs/Constants";
import * as SceneConfig from "_core/configs/SceneConfig";

export const timeCurrentIso8601 = () => (new Date()).toISOString();

// ファイルをbase64にエンコードする
export const encodeFileToBase64 = (fileBlob, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(fileBlob);
    reader.onload = () => {
        const base64 = reader.result;
        const dataPart = base64.split(',')[1]; // 'data:image/png;base64,' の後の部分を取得
        callback(dataPart);
    };
    reader.onerror = (error) => {
        console.error('Error converting file to base64!', error);
    };
};

// Base64文字列をBlobデータにデコードする
export const decodeBase64ToBlob = (base64, mimeType) => {
    // Base64文字列をバイナリデータに変換する
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    // 文字列をバイト配列に変換
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    // Uint8Arrayを作成
    const byteArray = new Uint8Array(byteNumbers);

    // Blobデータを作成
    return new Blob([byteArray], { type: mimeType });
};

// 現在時間を%Y-%m-%dT%H:%M:%S形式で取得する
export const getTime = () => {
    const now = new Date();

    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Tokyo'
    };

    const formattedDate = new Intl.DateTimeFormat('en-CA', options).format(now);

    // フォーマットを整える
    const [date, time] = formattedDate.split(', ');
    const formattedDateTime = date.replace(/\//g, '-') + ' ' + time;

    return formattedDateTime;
};

export const waitSecond = (seconds) => {
    return new Promise(resolve => setTimeout(resolve, (seconds * 1000)));  // 1000ミリ秒 = 1秒
}

export const ReverseKeyValue = (obj) => {
    const invertedObj = {};

    for (let key in obj) {
        const value = obj[key];
        
        // 重複するバリューがあった場合は無視
        if (!(value in invertedObj)) {
            invertedObj[value] = key;
        }
    }

    return invertedObj;
};


// AUTH_RANK_LIST上で、authがrequiredAuth以上の強さを持つか判定する
// authの数値を直接比較せず、リストの順序のみを根拠とする。
// authがnull/undefined（tAccount未取得）の場合はfalseを返す。
export const isAuthRankSatisfied = (auth, requiredAuth) => {
    const authIndex = Constants.AUTH_RANK_LIST.indexOf(Number(auth));
    const requiredIndex = Constants.AUTH_RANK_LIST.indexOf(requiredAuth);
    // 順位が未定義の権限は不許可
    if (authIndex < 0 || requiredIndex < 0) {
        return false;
    }

    return authIndex >= requiredIndex;
};


// 機微フィールドの閲覧権限を判定する（必要権限レベル以上ならtrue）
// サーバー側 CommonService.is_visible_field と同じ判定。authの数値を直接比較しない。
// authがnull/undefined（tAccount未取得）の場合はfalseを返す。
export const isVisibleField = (auth, fieldName) => {
    const requiredAuth = Constants.FIELD_REQUIRED_AUTH[fieldName];
    // 定義の無いフィールドは不許可
    if (requiredAuth === undefined) {
        return false;
    }

    return isAuthRankSatisfied(auth, requiredAuth);
};


// ユーザー管理（サブアカウント管理）の利用権限を判定する
// サーバー側 AdminService.can_manage_account と同じ判定。
// 画面側の判定はメニュー表示とページのガード（UX目的）のみに使用し、
// 実際の操作可否はサーバーが返す can_edit / can_delete 等のフラグに従うこと。
export const canManageAccount = (auth) => {
    return isAuthRankSatisfied(auth, Constants.ACCOUNT_MANAGE_REQUIRED_AUTH);
};


// 運営ツール（プロンプト管理・外見動画管理）の利用権限を判定する
// サーバー側 AdminService.can_use_ops_tool と同じ判定。画面側はメニュー表示とページのガードのみに使用する。
export const canUseOperatorTool = (auth) => {
    return isAuthRankSatisfied(auth, Constants.OPERATOR_TOOL_REQUIRED_AUTH);
};


// テナント作成の利用権限を判定する（運営ツールの権限があり、運営ホームテナントにログインしている）
// サーバー側 AdminService.is_ops_home_tenant と同じ判定。画面側はメニュー表示とページのガードのみに使用する。
export const canCreateTenant = (auth, tUser) => {
    return canUseOperatorTool(auth) && tUser?.config?.is_ops_home === true;
};


// ログイン後に開く管理画面（運営ホームテナントの運営はテナント作成、それ以外は基本情報）
export const adminLandingPage = (auth, tUser) => {
    return canCreateTenant(auth, tUser) ? SceneConfig.PAGES.PARTS_ADMIN_OPS_TENANT : SceneConfig.PAGES.ADMIN_USER_EDIT;
};


// 更新系の操作を行えない権限（一般（読み取りのみ））かを判定する
// サーバー側 admin_auth.py の deny_auth_list による拒否と対になる多層防御の画面側。
// 画面側は更新系UIを非活性にするUX目的のみで、実際の拒否はサーバーが行う。
// authがnull/undefined（tAccount未取得）や順位が未定義の権限の場合はtrue（＝読み取り専用扱い）を返す。
export const isReadOnlyAuth = (auth) => {
    return !isAuthRankSatisfied(auth, Constants.UPDATE_REQUIRED_AUTH);
};


export const FormatDate = (isoString=null) =>  {
    let date = new Date();
    if (isoString != null)
    {
        date = new Date(isoString);
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');  // 月は0から始まるので+1
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}年${month}月${day}日`;
}

export const FormatDateTime = (isoString=null) =>  {
    let date = new Date();
    if (isoString != null)
    {
        date = new Date(isoString);
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');  // 月は0から始まるので+1
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}年${month}月${day}日 ${hours}時${minutes}分`;
}

// 文字数を数える（サロゲートペアも1文字）
export const CountChars = (text) => [...(text || "")].length;

// 日本時間の日付を YYYYMMDD で返す（ファイル名用）
export const FormatDateCompactJst = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type).value;
    return `${part("year")}${part("month")}${part("day")}`;
}

// バイト数を表示用の文字列へ変換する(例: 104857600 → "100MB")
export const FormatFileSize = (bytes) => {
    const unitList = ["B", "KB", "MB", "GB"];

    let size = Number(bytes);
    if (! Number.isFinite(size) || size < 0)
    {
        return "";
    }

    let unitIndex = 0;
    while (size >= 1024 && unitIndex < (unitList.length - 1))
    {
        size /= 1024;
        unitIndex++;
    }

    // 端数がある場合のみ小数第1位まで表示する
    const rounded = Math.round(size * 10) / 10;

    return `${rounded}${unitList[unitIndex]}`;
}

export const HexToRgb =  (hex) => {
    // "#"を取り除く
    hex = hex.replace(/^#/, "");

    // 3桁の場合を6桁に展開
    if (hex.length === 3) {
        hex = hex.split("").map(char => char + char).join("");
    }

    // R, G, B値を取得
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
}

export const ConvertMarkdownToHTML = (markdown) => {
    // 1. 見出しを処理（# を <span class="header"> に変換）
    let html = markdown.replace(/^# (.+)$/gm, '<span class="text-size-h1">$1</span>');
    html = html.replace(/^## (.+)$/gm, '<span class="text-size-h2">$1</span>');
    html = html.replace(/^### (.+)$/gm, '<span class="text-size-h3">$1</span>');

    // 2. 強調を処理
    html = html.replace(/\*\*(.+?)\*\*/g, '<span class="bold">$1</span>'); // **太字**
    html = html.replace(/\*(.+?)\*/g, '<span class="italic">$1</span>');   // *斜体*

    // 3. 箇条書きを処理（- を <ul><li> に変換し、改行を無視）
    html = html.replace(/- (.+)$/gm, '・$1');

    // 4. リンクを処理（[text](url) を <a> に変換）
    html = html.replace(/^https:\/\/[^\s]+$/gm, '<a href="$&" target="_blank">$&</a>');

    // 5. 改行を処理（リスト外の改行のみ変換）
    html = html.replace(/\n(?!<\/?ul|<\/?li>)/g, '<br>');

    return html;
}


/**
 * SJIS/UTF-8混在のCSVをUTF-8に変換したFileとして返す
 * @param file アップロードされたCSVファイル
 * @returns UTF-8 に変換された File オブジェクト
 */
export const GetEncodedCSV = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // UTF-8に変換したバイト列（テキストではなく ArrayBuffer）
    const utf8Bytes = Encoding.convert(bytes, {
        to: "UTF8",
        from: Encoding.detect(bytes),
        type: "arraybuffer"
    });

    const utf8Blob = new Blob([utf8Bytes], { type: "text/csv;charset=utf-8" });

    const utf8File = new File([utf8Blob], file.name, {
        type: "text/csv",
        lastModified: Date.now(),
    });

    return utf8File;
};

export const DownloadCsvAsSJIS = async (url, filename = "download.csv") => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status}`);
    }

    const utf8Text = await response.text();
    console.log(utf8Text);

    // UTF-8 ⇒ SJIS に変換（Excel向け）
    const sjisBytes = Encoding.convert(utf8Text, {
        to: 'SJIS',
        from: 'UNICODE',
        type: 'arraybuffer',
    });

    const blob = new Blob([sjisBytes], { type: "text/csv" });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(blobUrl);
};

export const DownloadCsvAsUtf8BOM = async (url, filename = "download.csv") => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status}`);
    }

    const utf8Text = await response.text();
    console.log(utf8Text);  // 確認ログ

    // UTF-8 BOMを先頭に追加（Excel対策）
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, utf8Text], { type: "text/csv;charset=utf-8" });

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
};